/**
 * "The backend is not answering", "it cannot reach its database", and "this
 * deployment is half-migrated."
 *
 * None of the three fails cleanly. A tab left open across a backend outage
 * keeps rendering from what it already loaded, and every click fails with a
 * message about that click. A backend with no database boots, answers, and
 * then refuses every sign-in with a 500 that tells the person at the form
 * nothing. A backend on a schema behind its code signs you in, most screens
 * work, and then one of them returns a 500 — so the first thing anybody does is
 * look for a bug in that screen. This banner makes the actual cause the first
 * thing on the page instead of the last thing anybody checks.
 *
 * All three come off `GET /api/health`, most fundamental first: no backend
 * means nothing else it says is current, and while the database is down the
 * schema answer is stale — "run a migration" is the wrong instruction for a
 * database that is not there.
 *
 * Four decisions worth knowing:
 *
 * - **It renders on the sign-in screen too.** Any of the three stops anybody
 *   getting past sign-in to see a banner that lives behind it.
 * - **It cannot be dismissed.** Everything else on this screen is a normal
 *   piece of interface; this one says the server is broken underneath it. A
 *   dismissed banner is a broken deployment nobody is looking at any more.
 * - **"Not answering" includes "answering as something else".** `getHealth`
 *   checks the payload is ours, because in development `/api` is a proxy to a
 *   port, and another project's dev server on that port answers 200 with its
 *   own page.
 * - **It says nothing specific.** `GET /api/health` needs no session, so it
 *   reports a state and a count — never migration filenames, never the driver's
 *   error, which names hosts and database users. Those are in the server log,
 *   `forsys db:ping` and `forsys db:migrate:status`.
 *
 * It polls, like the bell does, and for the same reason: the fix happens
 * somewhere else — a restart, a terminal — so the banner has to notice it has
 * gone away without a reload of the page.
 *
 * It lives here rather than in `components/` because of that poll: a component
 * there may not fetch, and the two things in this application that watch the
 * server on a timer — this and the bell — are both parts of the shell.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import type { HealthResponse } from '@forsys/shared';
import { getHealth } from '../api/health';

/**
 * How often the state is re-checked while all is well.
 *
 * Paced by the faster of the two backend checks: the server pings its database
 * every fifteen seconds, and an outage is not deploy-shaped — it starts and
 * ends on its own. `/api/health` reads two cached values and touches nothing,
 * so this is the cheapest request the API has.
 */
const POLL_MS = 30_000;

/**
 * How soon to ask again after a failed check.
 *
 * Short, so that a backend coming back clears the banner within seconds — and
 * so that the second failure the banner waits for arrives quickly.
 */
const RETRY_MS = 5_000;

/**
 * Consecutive failures before the backend counts as down.
 *
 * Two, not one: a restart is a second or two of refused connections, and a
 * banner that flashes on every `systemctl restart` teaches people to ignore it.
 */
const FAILURES_BEFORE_BANNER = 2;

type View =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'silent' }
  | ({ readonly kind: 'answered' } & Pick<HealthResponse, 'schema' | 'database'>);

export function HealthBanner() {
  const { t } = useTranslation('system');
  const [view, setView] = useState<View>({ kind: 'waiting' });

  useEffect(() => {
    let alive = true;
    let failures = 0;
    let inFlight = false;
    let timer: number | undefined;

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(tick, failures > 0 ? RETRY_MS : POLL_MS);
    };

    const check = async () => {
      // The timer and a visibility change can both fire at once; two checks in
      // flight would count one outage as two failures.
      if (inFlight) return;
      inFlight = true;
      try {
        const health = await getHealth();
        if (!alive) return;
        failures = 0;
        setView({ kind: 'answered', schema: health.schema, database: health.database });
      } catch {
        // Refused, a proxy's 502, another server's HTML, a payload not ours —
        // all the same thing from here: what answers `/api` is not our backend.
        if (!alive) return;
        failures += 1;
        if (failures >= FAILURES_BEFORE_BANNER) setView({ kind: 'silent' });
      } finally {
        inFlight = false;
        if (alive) schedule();
      }
    };

    // A hidden tab keeps its schedule but does not ask; coming back asks at once.
    function tick() {
      if (document.visibilityState === 'visible') void check();
      else schedule();
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (view.kind === 'silent') {
    return (
      <Banner title={t('backendBanner.title')}>
        {t('backendBanner.body')} {t('backendBanner.fix')}
      </Banner>
    );
  }

  if (view.kind !== 'answered') return null;

  if (view.database.state === 'unreachable') {
    return (
      <Banner title={t('databaseBanner.title')}>
        {t('databaseBanner.body')} {t('databaseBanner.fix')}
      </Banner>
    );
  }

  /**
   * `unknown` shows nothing, for either backend check.
   *
   * It means nobody has looked yet — a process still starting — or, for the
   * schema, that the check itself failed. Neither is "your migrations are
   * behind", and claiming it would send somebody to run a migration that cannot
   * help.
   */
  if (view.schema.state === 'pending') {
    return (
      <Banner title={t('schemaBanner.title')}>
        {t('schemaBanner.body', { count: view.schema.pendingCount })} {t('schemaBanner.fix')}
      </Banner>
    );
  }

  return null;
}

// No outer padding of its own: it is placed inside whatever container the
// screen already has, so it lines up with that screen's content rather than
// with a second opinion about margins.
function Banner({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>
        {children}
      </Alert>
    </Box>
  );
}
